import gt from 'lodash/gt';
import lt from 'lodash/lt';

import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import lodashIsInteger from 'lodash/isInteger';

import isString from 'lodash/isString';
import { CHART_TAIL_TYPES } from '../store/scenes/distributions/chart/enums';

export const maxNumberOfBinsForHistogram = 500;

// atomic validators

export const isRequired = value => isNil(value) || (isString(value) && !value.trim()) ? 'Can not be blank!' : null;

export const isFloat = string => isNil(string) || !isNaN(+string) ? null : 'Should be a number!';

export const isInteger = string => (
  lodashIsInteger(+string)
    ? null
    : 'Should be an integer value!'
);


export { isFloat as isNumber };

export const isGreaterThan = minValue => value => (
  gt(value, minValue) ? null : `Should be greater than ${minValue}!`
);

export const isGreaterThanOrEqual = minValue => value => (
  gt(value, minValue) || minValue === value ? null : `Should be greater than or equal to ${minValue}!`
);

export const isLessThan = maxValue => value => (
  lt(value, maxValue) ? null : `Should be less than ${maxValue}!`
);

export const isLessThanOrEqual = maxValue => value => (
  lt(value, maxValue) || maxValue === value ? null : `Should be less than or equal to ${maxValue}!`
);

export const maxLength = max => value =>
  value && value.length > max ? `Must be ${max} characters or less!` : null;

// composite validators

const isRequiredNumber = numberValidator => value => isRequired(value) || numberValidator(value);

export const isRequiredFloat = isRequiredNumber(isFloat);
export const isRequiredInteger = isRequiredNumber(isInteger);

export { isRequiredFloat as isRequiredNumber };

const isRequiredNumberGreaterThan = requiredNumberValidator => minValue => (
  value => requiredNumberValidator(value) || isGreaterThan(minValue)(parseFloat(value))
);

const isRequiredNumberGreaterThanOrEqual = requiredNumberValidator => minValue => (
  value => requiredNumberValidator(value) || isGreaterThanOrEqual(minValue)(parseFloat(value))
);

const isRequiredNumberLessThan = requiredNumberValidator => maxValue => (
  value => requiredNumberValidator(value) || isLessThan(maxValue)(parseFloat(value))
);

const isRequiredNumberLessThanOrEqual = requiredNumberValidator => maxValue => (
  value => requiredNumberValidator(value) || isLessThanOrEqual(maxValue)(parseFloat(value))
);

export const isRequiredFloatGreaterThan = isRequiredNumberGreaterThan(isRequiredFloat);
export const isRequiredIntegerGreaterThan = isRequiredNumberGreaterThan(isRequiredInteger);

export const isRequiredFloatGreaterThanOrEqual = isRequiredNumberGreaterThanOrEqual(isRequiredFloat);
export const isRequiredIntegerGreaterThanOrEqual = isRequiredNumberGreaterThanOrEqual(isRequiredInteger);

export { isRequiredFloatGreaterThan as isRequiredNumberGreaterThan };

export const isRequiredFloatLessThan = isRequiredNumberLessThan(isRequiredFloat);
export const isRequiredIntegerLessThan = isRequiredNumberLessThan(isRequiredInteger);

export const isRequiredFloatLessThanOrEqual = isRequiredNumberLessThanOrEqual(isRequiredFloat);
export const isRequiredIntegerLessThanOrEqual = isRequiredNumberLessThanOrEqual(isRequiredInteger);

export const makeIntegerRangeValidator = (minValue, maxValue) => {
  const error = `Should be an integer greater than or equal to ${minValue} and less than or equal to ${maxValue}!`;
  return value => value >= minValue && value <= maxValue && isInteger(value) === null ? null : error;
};

export const makeRangeValidator = (minValue, maxValue) => {
  const error = `Should be greater than or equal to ${minValue} and less than or equal to ${maxValue}!`;
  return value => !isRequired(value) && value >= minValue && value <= maxValue ? null : error;
};

export const makeFloatRangeValidator = (minValue, maxValue) => {
  const error = `Should be a number greater than or equal to ${minValue} and less than or equal to ${maxValue}!`;
  return value => !isRequired(value) && value >= minValue && value <= maxValue ? null : error;
};

export const makeIntegerRangeValidatorWithLabelsLower = (minValue, maxValue, minValueLabel, maxValueLabel) => {
  const error = `Should be an integer greater than or equal to ${minValueLabel} and less than ${maxValueLabel}!`;
  return value => isRequiredIntegerGreaterThanOrEqual(minValue)(value) || isRequiredIntegerLessThan(maxValue)(value) ? error : null;
};

export const makeSampleSizeValidator = maxValue => makeIntegerRangeValidator(1, maxValue);

export const numberOfSamplesValidator =
  makeIntegerRangeValidator(2, 10000);

export const proportionValidator =
  makeFloatRangeValidator(0, 1);

export const alphaValueValidator = makeFloatRangeValidator(0.00001, 0.99999);

// -5.2649, 5.2649 are the default max and min allowed values for x when mean is 0 and stdev is 1;
// the same here, so that logic for cutoffs and alpha is not changed
// Also, since z is a relative value, it is not dynamically calculated, like cutoffs are
export const zScoreValueValidator = makeFloatRangeValidator(-5.2649, 5.2649);

export { makeFloatRangeValidator as makeCutoffValidator };

export const makeCutoffOrZscoreIntersectionValidator = (leftValue, rightValue, tailType, isBinomialDistribution = false) => {
  let error = null;

  if (tailType === CHART_TAIL_TYPES.LEFT || tailType === CHART_TAIL_TYPES.RIGHT || tailType === CHART_TAIL_TYPES.EQUAL) {
    // this validation is not needed when either Left or Right tail is selected, hence exit
    return null;
  } else if (tailType === CHART_TAIL_TYPES.BOTH) {
    error = 'Please check your endpoints.  The intervals should not overlap.';
    if (isBinomialDistribution) {
      return leftValue <= rightValue ? null : error;
    }
    return leftValue < rightValue ? null : error;
  } else {
    error = 'Please check your endpoints.  Your intervals are in the tails instead of between.';
    // always CENTER, works like reversed BOTH
    if (isBinomialDistribution) {
      return leftValue <= rightValue ? null : error;
    }
    return leftValue < rightValue ? null : error;
  }
};

export const makeBinWidth = minValue => value => (
  gt(value, minValue) ? null : `Can not display more than ${maxNumberOfBinsForHistogram} bins!`
);

export const makeSettingsHistogramValidator = minValue => value => (
  isRequired(value) || makeBinWidth(minValue)(value)
);
